// Story 8.1 (ADO-2010), Task 6.1 — the Depot Switcher (Figma "Depot Switcher · Open",
// 5080:7247; mobile 5332:67565; tablet 5405:22656).
//
// Opened from the header depot tab, it lists every place the Shopper can order at
// (one tile per entry and Trading Account) and marks the current one, which it lists first (then the rest in the order given, so the
// tab order is the same). On desktop it is a white band of 220px tiles under the
// branding bar, scrolled sideways with the carousel arrows (Figma "Carousel Arrow":
// shown where the pointer cannot scroll sideways, disabled at either end). On tablet
// and mobile it is a dropdown list that hangs from the depot box over the page, so the
// header keeps its height. A dropdown row follows Figma "Dropdown Option" (5013:40543;
// mobile 5332:67592, tablet 5405:22908): the name over a sub-line holding the account line
// and the cut-off pill, then a fixed trailing pair, the count badge and a 16px check slot
// reserved on every row, so the badges and the tick line up down the list. Both the band
// tiles and the dropdown rows carry the open-cart line count badge (Story 8.1 A9.1).
//
// Presentational: the host passes the options and handles the pick. Like the old
// dropdown it is a plain disclosure button group (no ARIA menu model), each option a
// button with `aria-current` on the active one.
//
// Story 8.5 (ADO-2014), D-20: each option can carry its depot's cut-off pill. The host
// renders it through `renderOptionCutOff` (the panel never fetches, like `CutOffPill`):
// `small` on the band, in the tile's foot row beside "Current", and `compact` on the
// dropdown rows, in the sub-line after the account line. The pill is presentational inside the
// option button; the header's one `CutOffAnnouncer` is the live region.

import { type ReactNode, type Ref, useCallback, useEffect, useRef, useState } from 'react';
import { linesLabel } from '../../basket/cartTotals';
import type { CutOffPillSize } from '../CutOffPill/CutOffPill';
import { Icon } from '../Icon/Icon';
import styles from './DepotSwitcherPanel.module.css';

/** One pickable place in the switcher. */
export interface SiteHeaderDepotOption {
  readonly id: string;
  /** The entry / depot label ("VC Dublin", "Fruit & Veg"). */
  readonly name: string;
  /** The account line ("Main grocery · 3205484"), or the account number alone. */
  readonly detail?: string | null | undefined;
  /** Lines in that depot's open cart; the count badge shows when > 0. */
  readonly lineCount?: number | null | undefined;
  /**
   * The depot's 16x16 Figma icon (5080:7247 "Icon slot"), a public asset path; the generic
   * depots glyph shows when absent.
   */
  readonly iconSrc?: string | null | undefined;
}

export interface DepotSwitcherPanelProps {
  readonly id: string;
  readonly options: readonly SiteHeaderDepotOption[];
  readonly activeId: string | null | undefined;
  readonly onSelect: (id: string) => void;
  /** `band`: the desktop tile band. `dropdown`: the tablet / mobile list. */
  readonly variant: 'band' | 'dropdown';
  readonly panelRef?: Ref<HTMLDivElement>;
  /** The option's cut-off pill at the panel's size, or `null` for none (Story 8.5, D-20). */
  readonly renderOptionCutOff?: ((optionId: string, size: CutOffPillSize) => ReactNode) | undefined;
  /**
   * ADO-2262: the dropdown is running its exit (held mounted by the host): it takes no input
   * and is out of the accessibility tree.
   */
  readonly closing?: boolean | undefined;
  /** ADO-2262: the dropdown's exit has ended (its own opacity transition). */
  readonly onExited?: (() => void) | undefined;
}

/** The count badge text: the Figma caps at "99+". */
function countText(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/** The options with the current one first and the rest in their given order. */
export function currentFirst(
  options: readonly SiteHeaderDepotOption[],
  activeId: string | null | undefined,
): readonly SiteHeaderDepotOption[] {
  const current = options.find((option) => option.id === activeId);
  return current === undefined
    ? options
    : [current, ...options.filter((option) => option !== current)];
}

/** The option's accessible name: name, account line, then the cart state the badge shows. */
export function depotOptionLabel(option: SiteHeaderDepotOption): string {
  const parts = [option.name];
  if (option.detail) parts.push(option.detail);
  if ((option.lineCount ?? 0) > 0) parts.push(`${linesLabel(option.lineCount ?? 0)} in cart`);
  return parts.join(', ');
}

export function DepotSwitcherPanel({
  id,
  options,
  activeId,
  onSelect,
  variant,
  panelRef,
  renderOptionCutOff,
  closing = false,
  onExited,
}: DepotSwitcherPanelProps) {
  const band = variant === 'band';
  const cutOffSize: CutOffPillSize = band ? 'small' : 'compact';
  const trackRef = useRef<HTMLUListElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const measure = useCallback(() => {
    const track = trackRef.current;
    if (track === null) return;
    setCanScrollBack(track.scrollLeft > 0);
    setCanScrollForward(track.scrollLeft + track.clientWidth < track.scrollWidth - 1);
  }, []);

  useEffect(() => {
    if (!band) return;
    measure();
    const track = trackRef.current;
    const observer =
      typeof ResizeObserver !== 'undefined' && track !== null ? new ResizeObserver(measure) : null;
    if (track !== null) observer?.observe(track);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [band, measure, options.length]);

  // Bring the current tile into view when the band opens (it is first, so this keeps the
  // band at its start). The band renders apart from its trigger (after the branding bar,
  // ahead of the themed sub-nav that holds the trigger), so the next Tab would skip it:
  // focus moves to the current tile, or the first, on open (WCAG 2.4.3). The dropdown
  // follows its trigger in the DOM, so focus stays on the trigger there.
  useEffect(() => {
    const track = trackRef.current;
    const current = track?.querySelector<HTMLElement>('[aria-current="true"]');
    current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    if (band)
      (current ?? track?.querySelector<HTMLElement>('button'))?.focus({ preventScroll: true });
  }, [band]);

  const scrollBy = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (track === null) return;
    track.scrollBy({ left: direction * Math.max(track.clientWidth - 92, 232), behavior: 'smooth' });
  };

  return (
    <div
      id={id}
      ref={panelRef}
      className={styles.panel}
      data-layout={variant}
      data-closing={closing ? '' : undefined}
      inert={closing}
      aria-hidden={closing ? true : undefined}
      onTransitionEnd={(event) => {
        if (closing && event.target === event.currentTarget && event.propertyName === 'opacity') {
          onExited?.();
        }
      }}
    >
      {band && (
        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowBack}`}
          aria-label="Scroll depots back"
          disabled={!canScrollBack}
          onClick={() => scrollBy(-1)}
        >
          <Icon name="caretLeft" size={18} />
        </button>
      )}
      <div className={styles.viewport}>
        <ul ref={trackRef} className={styles.track} aria-label="Choose depot" onScroll={measure}>
          {currentFirst(options, activeId).map((option) => {
            const current = option.id === activeId;
            const count = option.lineCount ?? 0;
            const cutOff = renderOptionCutOff?.(option.id, cutOffSize) ?? null;
            return (
              <li key={option.id} className={styles.item}>
                <button
                  type="button"
                  className={styles.tile}
                  aria-current={current ? 'true' : undefined}
                  aria-label={depotOptionLabel(option)}
                  onClick={() => onSelect(option.id)}
                >
                  <span className={styles.iconSlot} aria-hidden>
                    {option.iconSrc ? (
                      <img src={option.iconSrc} width={16} height={16} alt="" />
                    ) : (
                      <Icon name="depots" size={16} />
                    )}
                  </span>
                  {band ? (
                    <span className={styles.body}>
                      <span className={styles.titleRow}>
                        <span className={styles.title}>{option.name}</span>
                        {count > 0 && (
                          <span className={styles.count} data-part="count" aria-hidden>
                            {countText(count)}
                          </span>
                        )}
                        {current && (
                          <span className={styles.check} aria-hidden>
                            <Icon name="check" size={12} />
                          </span>
                        )}
                      </span>
                      {option.detail ? (
                        <span className={styles.detail}>{option.detail}</span>
                      ) : null}
                      {(current || cutOff !== null) && (
                        <span
                          className={styles.foot}
                          data-cut-off-slot={cutOff !== null || undefined}
                        >
                          {current && (
                            <span className={styles.status} aria-hidden>
                              Current
                            </span>
                          )}
                          {cutOff}
                        </span>
                      )}
                    </span>
                  ) : (
                    <>
                      <span className={styles.body}>
                        <span className={styles.title}>{option.name}</span>
                        {(option.detail || cutOff !== null) && (
                          <span className={styles.subLine} data-part="sub-line">
                            {option.detail ? (
                              <span className={styles.detail}>{option.detail}</span>
                            ) : null}
                            {cutOff !== null && (
                              <span className={styles.cutOff} data-cut-off-slot>
                                {cutOff}
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                      {count > 0 && (
                        <span className={styles.count} data-part="count" aria-hidden>
                          {countText(count)}
                        </span>
                      )}
                      <span className={styles.checkSlot} data-part="check-slot" aria-hidden>
                        {current && <Icon name="check" size={16} />}
                      </span>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        {band && canScrollBack && <span className={styles.fadeBack} aria-hidden />}
        {band && canScrollForward && <span className={styles.fadeForward} aria-hidden />}
      </div>
      {band && (
        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowForward}`}
          aria-label="Scroll depots forward"
          disabled={!canScrollForward}
          onClick={() => scrollBy(1)}
        >
          <Icon name="caretRight" size={18} />
        </button>
      )}
    </div>
  );
}
